/**
 * COMPOSANT UI: MODAL
 * Modal réutilisable avec design système cohérent
 */

import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useRef } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
};

export function Modal({ isOpen, onClose, title, children, footer, size = 'md' }: ModalProps) {
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  if (!isOpen) return null;

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Enregistrer où le clic a commencé
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    // Ne fermer que si le mousedown ET le mouseup sont sur l'overlay (pas sur le contenu)
    if (mouseDownTargetRef.current === e.currentTarget && e.target === e.currentTarget) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className={cn('bg-white rounded-lg shadow-lg w-full', sizeClasses[size])}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <h2 className="text-lg font-semibold text-zinc-900">{title}</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center size-9 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
            type="button"
            aria-label="Fermer"
          >
            <X className="size-4 text-zinc-900" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[80vh] overflow-y-auto">{children}</div>

        {/* Footer (optional) */}
        {footer && <div className="p-6 border-t border-zinc-200">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

interface ModalFooterProps {
  onCancel: () => void;
  onSubmit?: () => void;
  cancelLabel?: string;
  submitLabel?: string;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  formId?: string; // ID du formulaire à soumettre
}

export function ModalFooter({
  onCancel,
  onSubmit,
  cancelLabel = 'Annuler',
  submitLabel = 'Valider',
  isSubmitting = false,
  submitDisabled = false,
  formId,
}: ModalFooterProps) {
  return (
    <div className="flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="px-4 py-2 text-sm font-medium text-zinc-900 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
        disabled={isSubmitting}
      >
        {cancelLabel}
      </button>
      <button
        type="submit"
        form={formId} // Lien avec le formulaire via l'ID
        onClick={onSubmit}
        disabled={isSubmitting || submitDisabled}
        className="px-4 py-2 text-sm font-medium text-white bg-zinc-900 rounded-md shadow-md hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Chargement...' : submitLabel}
      </button>
    </div>
  );
}
